import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Legend,
  Title,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { saveAs } from 'file-saver';
import styled from 'styled-components';
import tw from 'twin.macro';
import strings from '@/language/Swedish';
import { IndexValueType } from '@/types/apiTypes';
import { valueDate } from './indexViewerConfig';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Legend,
  Title,
  Tooltip
);

const ChartContainer = styled.div`
  ${tw`relative w-full h-full min-h-72`};
`;

interface ChartSeriesType {
  id: string;
  name: string;
  color: string;
  values: IndexValueType[];
}

interface Props {
  series: ChartSeriesType[];
  hoveredId?: string | null;
  onSeriesHover?: (id: string | null) => void;
}

interface IndexCompareChartHandle {
  exportPng: (fileName: string) => void;
}

const numberFormat = new Intl.NumberFormat('sv-SE', {
  maximumFractionDigits: 2,
});

const IndexCompareChart = forwardRef<IndexCompareChartHandle, Props>(
  ({ series, hoveredId, onSeriesHover }, ref) => {
    const chartRef = useRef<ChartJS<'line'> | null>(null);

  useImperativeHandle(ref, () => ({
    exportPng: (fileName: string) => {
      const canvas = chartRef.current?.canvas;
      if (!canvas) return;

      // Chart canvas is transparent; paint it onto a white background and add
      // a Valueguard attribution strip at the bottom
      const ratio = window.devicePixelRatio || 1;
      const footerHeight = 32 * ratio;
      const output = document.createElement('canvas');
      output.width = canvas.width;
      output.height = canvas.height + footerHeight;
      const context = output.getContext('2d');
      if (!context) return;

      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, output.width, output.height);
      context.drawImage(canvas, 0, 0);

      context.fillStyle = '#64748b';
      context.font = `${12 * ratio}px Helvetica, Arial, sans-serif`;
      context.textAlign = 'right';
      context.textBaseline = 'middle';
      context.fillText(
        strings.indexApp.exportAttribution,
        output.width - 16 * ratio,
        canvas.height + footerHeight / 2
      );

      output.toBlob((blob) => {
        if (blob) saveAs(blob, `${fileName}.png`);
      }, 'image/png');
    },
  }));

  const { labels, datasets } = useMemo(() => {
    const months = new Set<string>();
    series.forEach((item) =>
      item.values.forEach((value) => months.add(valueDate(value).substring(0, 7)))
    );
    const sortedLabels = Array.from(months).sort();

    return {
      labels: sortedLabels,
      datasets: series.map((item) => {
        const valueByMonth = new Map(
          item.values.map((value) => [
            valueDate(value).substring(0, 7),
            value.value,
          ])
        );
        const isDimmed = Boolean(hoveredId) && item.id !== hoveredId;
        const isHighlighted = item.id === hoveredId;

        return {
          label: item.name,
          data: sortedLabels.map((month) => valueByMonth.get(month) ?? null),
          // 26 = ~15% alpha on the 6-digit hex while a sibling is hovered
          borderColor: isDimmed ? `${item.color}26` : item.color,
          backgroundColor: item.color,
          borderWidth: isHighlighted ? 3.5 : 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0,
          spanGaps: true,
        };
      }),
    };
  }, [series, hoveredId]);

  return (
    <ChartContainer>
      <Line
        ref={chartRef}
        data={{ labels, datasets }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            // The pie chart registers the datalabels plugin globally; keep
            // point values off this chart or every point gets a printed number
            datalabels: { display: false },
            legend: {
              display: true,
              position: 'bottom',
              labels: { usePointStyle: true, boxWidth: 8, boxHeight: 8 },
              onHover: (_event, legendItem) => {
                const item = series[legendItem.datasetIndex ?? -1];
                onSeriesHover?.(item ? item.id : null);
              },
              onLeave: () => onSeriesHover?.(null),
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: (context) =>
                  `${context.dataset.label}: ${
                    context.parsed.y === null
                      ? '-'
                      : numberFormat.format(context.parsed.y)
                  }`,
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { maxTicksLimit: 12, maxRotation: 0 },
            },
            y: {
              beginAtZero: false,
              grid: { color: 'rgb(30 41 59 / 5%)' },
              ticks: {
                callback: (value) => numberFormat.format(Number(value)),
              },
            },
          },
        }}
      />
    </ChartContainer>
  );
  }
);

IndexCompareChart.displayName = 'IndexCompareChart';

export default IndexCompareChart;
export type { ChartSeriesType, IndexCompareChartHandle };
